import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { useCareersHeader } from '../../utilities/static-queries/careers-header';
import { Row, Column, Icon, List, Container } from '@pella/sash-framework';
import { Image, CTA } from '../../';
import { isValidArray } from '../../utilities/validations';
import Dropdown from './dropdown';
import SiteSearch from './search';
import styles from './index.module.scss';

const CareersHeader = ({ showMenu, onHamburgerClick, activePage }) => {
	const data = useCareersHeader();
	const scrollYOffset = useRef(0);
	const [showSearch, setShowSearch] = useState(false);
	const [loading, setLoading] = useState(true);
	const [scrollClass, setScrollClass] = useState('');

	useEffect(() => {
		setLoading(false);

		document.addEventListener('scroll', () => onPageScroll(), {
			passive: true,
		});
		return () => document.removeEventListener('scroll', () => onPageScroll());
	}, []);

	useEffect(() => {
		if (!loading && activePage.hash) {
			let element = document.getElementById(activePage.hash.substring(1));
			element?.scrollIntoView({ behavior: 'smooth' });

			// Close menu by triggering click
			document.body.click();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [activePage]);

	// Header Scroll Updates
	const onPageScroll = () => {
		if (typeof window !== 'undefined') {
			const offset = window.pageYOffset;

			// Remove updates from scrolling
			if (offset === 0 || (offset <= 150 && window.innerWidth > 1030)) {
				setScrollClass('');
			}

			// Trigger the desktop slimmer menu
			if (offset > 150 && offset > scrollYOffset.current) {
				setScrollClass('scrollingDown');
			}

			// Trigger the re-appearance in mobile on scroll up
			if (offset > 150 && offset < scrollYOffset.current) {
				setScrollClass('scrollingUp');
			}

			scrollYOffset.current = offset;
		}
	};

	return (
		!loading && (
			<header
				id='js-header'
				className={`${styles.header} ${showMenu || showSearch ? styles.header_openMenu : ''} ${styles[scrollClass]}`}
			>
				<Row
					align='center'
					className={styles.header_ParentRow}
					noGutters={true}
				>
					<Column xs='auto'>
						<Link
							to='/'
							title='Link to Pella.com homepage'
							className={styles.header_LogoLink}
						>
							<img
								src='https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt33d368eeba60c44c/pella-logo-black-spot.svg'
								alt=''
								className={styles.header_Logo}
							/>
						</Link>
					</Column>

					<Column
						xs='fluid'
						className={styles.removePosition}
					>
						<div className={styles.header_TopSection} />

						<div className={styles.header_MainSection}>
							<Row
								align='center'
								noGutters={true}
							>
								<Column
									xs='fluid'
									className={`${styles.header_MainNavColumn} ${styles.removePosition}`}
								>
									{data.navigation?.menu_items?.[0] && (
										<List
											bullets='none'
											layout='inline'
											className={styles.header_NavList}
											role='navigation'
											aria-label='Main Navigation'
										>
											{data.navigation.menu_items.map((link, index) => {
												if (isValidArray(link?.dropdown)) {
													return (
														<li key={index}>
															<Dropdown data={link}>
																<Container fluid>
																	<Row>
																		{link.dropdown.map((item, index) => (
																			<Column
																				key={index}
																				xs='fluid'
																			>
																				<CTA data={item.ctas[0]}>
																					{item.image?.[0] && (
																						<Image
																							data={item.image[0]}
																							className='!block w-full'
																						/>
																					)}

																					{item.heading && <h3>{item.heading}</h3>}

																					{item.description && <p>{item.description}</p>}
																				</CTA>
																			</Column>
																		))}
																	</Row>
																</Container>
															</Dropdown>
														</li>
													);
												} else {
													return <li key={index}>{link.ctas?.[0] && <CTA data={link.ctas[0]} />}</li>;
												}
											})}
										</List>
									)}
								</Column>

								{/* Search Icon and Form */}
								<Column
									xs='auto'
									sm='fluid'
									lg='auto'
									className={styles.header_SearchColumn}
								>
									<button
										className={`items-center ${styles.header_SearchTrigger} ${showSearch ? styles.header_SearchTrigger_active : ''}`}
										onClick={() => setShowSearch(!showSearch)}
									>
										<span className={styles.header_SrOnly}>Search</span>
										<Icon name={showSearch ? 'close' : 'search'} />
										<span className={styles.header_mobileTrigger_label}>Search</span>
									</button>

									<SiteSearch mobileSearch={showSearch} />
								</Column>

								{/* { Hamburger } */}
								<Column
									xs='auto'
									className={styles.header_MobileColumn}
								>
									<button
										className={styles.header_MobileTrigger}
										onClick={onHamburgerClick}
									>
										<span className={styles.header_SrOnly}>Mobile Menu</span>
										{!showMenu ? (
											<Icon
												name='menu'
												className={styles.header_MobileIcon}
											/>
										) : (
											<Icon
												name='close'
												className={styles.header_MobileIcon}
											/>
										)}
										<span className={styles.header_mobileTrigger_label}>Menu</span>
									</button>
								</Column>
							</Row>
						</div>
					</Column>
				</Row>
			</header>
		)
	);
};

CareersHeader.propTypes = {
	data: PropTypes.object,
	showMenu: PropTypes.bool,
	onHamburgerClick: PropTypes.func,
	activePage: PropTypes.object,
};

export default CareersHeader;
